import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ActiveService } from 'src/app/project/services/active/active.service';
import { CombineCustomFieldsService } from '../../../custom-fields/combine-custom-fields/combine-custom-fields.service';
import { toggleItemAndDescendants } from '../../../custom-fields/custom-field-multi-list-select/custom-field-multi-list-select-utils';
import { ECustomFieldType } from '../../../custom-fields/custom-field-types-enums';
import { TCustomFieldList } from '../../../custom-fields/custom-fields.model';
import { GET_CUSTOM_FIELDS } from '../../../custom-fields/custom-fields.store';
import { EAdvancedFilterOptions } from '../../../filters-advanced/models/advanced-filter-options.enum';
import { EAdvancedFilterTimelineDate } from '../../../filters-advanced/models/advanced-filter-timeline-date.enum';
import { EAdvancedFilterType } from '../../../filters-advanced/models/advanced-filter-type.enum';
import {
  TAdvancedFilterTimeline,
  TSearchFilter,
  TSearchFilterDTO,
  TSearchFilterRow,
} from '../../../filters-advanced/models/advanced-filter.model';
import {
  EPointSearchOperator,
  TSearchAdvancedFilterOperator,
  TSearchFilterSubgroup,
  TSearchResponseDTO,
} from '../../../points/point-search/point-search.consts';
import { checkCustomWorkspaceId } from '../../../workspace/workspace';
import { EWorkspaces } from '../../../workspace/workspaces.enum';
import { endOfDayUTC, startOfDayUTC } from '../../utils/date-utc.utils';
import { findIdByPositionPath } from '../../utils/find-id-by-position-path';
import { findPositionPath } from '../../utils/find-position-path';
import { getDefaultQuickFilters } from '../../utils/get-default-quick-filters';
import { isNonEmptyString } from '../../utils/is-non-empty-string';
import { generateBetweenFilter } from './generate-between-filter';
import { TFilters } from './quick-filter-service.consts';
import { EQuickFilterKey, EQuickFilterTranslatedKey } from './quick-filter.consts';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';

@Injectable({
  providedIn: 'root',
})
export class QuickFilterService {
  private filters: TFilters = {
    quick: [],
    advanced: [],
    operator: 'AND',
  };

  keyword = '';

  private readonly _filtersChange$ = new Subject<TFilters>();
  private readonly filtersChange$ = this._filtersChange$.asObservable();
  hasFilterByIdEnabled = false;

  constructor(
    private activeService: ActiveService,
    private combineCustomFieldsService: CombineCustomFieldsService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.hasFilterByIdEnabled = this.featureFlagService.getFlag(EFeatureFlag.FILTER_BY_ID);

    this.featureFlagService.flagsChange$.subscribe(() => {
      this.hasFilterByIdEnabled = this.featureFlagService.getFlag(EFeatureFlag.FILTER_BY_ID);
    });
  }

  setFilters(filters: TFilters): void {
    this.filters.advanced = filters.advanced.filter((filter) => filter.values); // removes half-finished conditions
    this.filters.quick = filters.quick;
    this.filters.operator = filters.operator;
    this.emitFiltersChange();
  }

  getKeyword(): string {
    return this.keyword;
  }

  setKeyword(keyword: string): void {
    this.keyword = keyword;
  }

  getFilters(): TFilters {
    return this.filters;
  }

  getFiltersChange$(): Observable<TFilters> {
    return this.filtersChange$;
  }

  resetFilters(): void {
    this.filters.quick = getDefaultQuickFilters();
    this.filters.operator = 'AND';
    this.filters.advanced = [];
    this.keyword = '';
    this.emitFiltersChange();
  }

  /** Transforms filters to the format required by the API */
  getFiltersTransformed(): TSearchResponseDTO['filter'] {
    const quickFilters = this.filters.quick;
    const allFilters = [...this.filters.advanced];

    const quickFiltersTransformed: TSearchFilter[] = quickFilters
      .filter((filter) => filter.operator && filter.values?.length && filter.keys?.length)
      .filter((filter) => {
        const matchingAdvancedFilter = allFilters.find((advFilter) =>
          advFilter.keys.some((key) => filter.keys.includes(key)),
        );
        return !matchingAdvancedFilter;
      })
      .flatMap((filter) => (filter.values ? this.transformFilter(filter) : []));

    const advancedFiltersTransformed: TSearchFilterSubgroup[] = [];

    allFilters
      .filter((filter) => filter.operator && filter.values?.length && filter.keys?.length)
      .forEach((filterRow) => {
        if (!filterRow.values) {
          return;
        }

        const transformed: TSearchFilter = this.transformFilter(filterRow);

        const timelineDate: EAdvancedFilterTimelineDate = (
          filterRow.item as TAdvancedFilterTimeline
        )?.date;

        if (timelineDate === EAdvancedFilterTimelineDate.IS_BETWEEN) {
          advancedFiltersTransformed.push(this.generateBothTimelinePositionsFilter(transformed));
        } else {
          advancedFiltersTransformed.push(...this.generateAdvancedFilters([transformed]));
        }
      });

    const keywordTransformed = this.generateKeywordFilters(this.keyword);

    const newQuickFilters = quickFiltersTransformed.length ? quickFiltersTransformed : [];

    if (
      !newQuickFilters.length &&
      !advancedFiltersTransformed.length &&
      !keywordTransformed.length
    ) {
      return undefined;
    }

    const result: TSearchResponseDTO['filter'] = this.generateFiltersDTO(
      newQuickFilters.map((filter) => ({
        values: filter.values,
        operator: filter.operator,
        key: filter.keys[0],
      })),
      advancedFiltersTransformed,
      keywordTransformed,
    );

    return result;
  }

  private generateFiltersDTO(
    newQuickFilters: TSearchFilterDTO[],
    advancedFiltersTransformed: TSearchFilterSubgroup[],
    keywordTransformed: TSearchFilterSubgroup[],
  ): {
    groupOperator: TSearchAdvancedFilterOperator;
    groups: TSearchFilterSubgroup[];
    criteria: TSearchFilterDTO[];
  } {
    const result: TSearchResponseDTO['filter'] = {
      groupOperator: 'AND',
      groups: [],
      criteria: [],
    };

    if (newQuickFilters.length) {
      result.groups.push({
        criteriaOperator: 'AND',
        criteria: [],
        subgroups: [
          {
            criteria: newQuickFilters,
          },
        ],
      });
    }

    if (advancedFiltersTransformed.length) {
      result.groups.push({
        criteriaOperator: this.filters.operator,
        subgroups: advancedFiltersTransformed,
        criteria: [],
      });
    }

    if (keywordTransformed.length) {
      result.groups.push({
        criteriaOperator: 'OR',
        criteria: [],
        subgroups: keywordTransformed,
      });
    }

    return result;
  }

  private generateAdvancedFilters(filters: TSearchFilter[]): TSearchFilterSubgroup[] {
    const newFilters: TSearchFilterSubgroup[] = [];

    filters.forEach((filter) => {
      switch (filter.operator) {
        case EPointSearchOperator.BETWEEN:
          newFilters.push(generateBetweenFilter(filter));
          break;
        default:
          switch (filter.keys[0]) {
            case 'location':
              if (filter.values[0]) {
                newFilters.push(this.generateLocationFilter(filter));
              }
              break;
            default: {
              const result: TSearchFilterSubgroup = {
                criteriaOperator: 'OR',
                criteria: [],
                subgroups: [],
              };

              const naturalValues = this.convertToNaturalValues(filter);
              filter.keys.forEach((key) => {
                const perKey = filter.valuesByKey?.[key];
                result.criteria.push({
                  values: perKey ?? naturalValues,
                  operator: filter.operator,
                  timelinePosition: filter.timelinePosition,
                  key: key,
                });
              });

              newFilters.push(result);
              break;
            }
          }
      }
    });

    return newFilters;
  }

  private generateBothTimelinePositionsFilter(filter: TSearchFilter): TSearchFilterSubgroup {
    const startFilter: TSearchFilter = { ...filter, timelinePosition: 'START' };
    const endFilter: TSearchFilter = { ...filter, timelinePosition: 'END' };

    const startSubgroups: TSearchFilterSubgroup[] = this.generateAdvancedFilters([startFilter]);
    const endSubgroups: TSearchFilterSubgroup[] = this.generateAdvancedFilters([endFilter]);

    return {
      criteriaOperator: 'AND',
      subgroups: [...startSubgroups, ...endSubgroups],
    };
  }

  convertToNaturalValues(filter: TSearchFilter): unknown[] {
    const isCheckbox = GET_CUSTOM_FIELDS()[filter.keys[0]]?.type === ECustomFieldType.CHECKBOX;

    if (isCheckbox) {
      return filter.values.map((value) =>
        value === 'true' || value === 'false' ? JSON.parse(value) : value,
      );
    }

    switch (filter.keys[0]) {
      case 'flagged':
        return filter.values.map((value) =>
          value === 'true' || value === 'false' ? JSON.parse(value) : value,
        );
      case 'sequenceNumber':
        return filter.values.map((value) => +value);
      default:
        return filter.values;
    }
  }

  private generateLocationFilter(filter: TSearchFilter): TSearchFilterSubgroup {
    const criteriaOperator = filter.values[0] === EAdvancedFilterOptions.EXISTS ? 'OR' : 'AND';
    const operator =
      filter.values[0] === EAdvancedFilterOptions.EXISTS
        ? EPointSearchOperator.NOT_IN
        : EPointSearchOperator.IN;

    return {
      criteriaOperator: criteriaOperator,
      criteria: [
        {
          key: 'pins',
          operator: operator,
          values: ['', null],
        },
        {
          key: 'polygons',
          operator: operator,
          values: ['', null],
        },
      ],
    };
  }

  private generateKeywordFilters(keyword: string): TSearchFilterSubgroup[] {
    if (!keyword) {
      return [];
    }

    const newFilters: TSearchFilterSubgroup[] = [];
    const workspaceId = checkCustomWorkspaceId() || this.activeService.getActiveWorkspaceId();
    const searchedCustomFields: string[] = [];
    const customFields = GET_CUSTOM_FIELDS();

    Object.keys(customFields).forEach((customFieldId) => {
      const customField = customFields[customFieldId];
      if (
        (customField.type === ECustomFieldType.TEXT ||
          customField.type === ECustomFieldType.RICHTEXT) &&
        (customField.workspaceId === workspaceId || workspaceId === EWorkspaces.OVERVIEW)
      ) {
        searchedCustomFields.push(customFieldId);
      }
    });

    const searchedKeys = [
      'title',
      'description',
      'accountName',
      'siteName',
      ...searchedCustomFields,
    ];

    searchedKeys.forEach((key) => {
      newFilters.push({
        criteriaOperator: 'OR',
        subgroups: [],
        criteria: [
          {
            key: key,
            operator: EPointSearchOperator.REGEX,
            values: [keyword],
          },
        ],
      });
    });

    const isNumber = Number.isNaN(Number(keyword)) === false;

    if (this.hasFilterByIdEnabled && isNumber) {
      newFilters.push({
        criteriaOperator: 'OR',
        subgroups: [],
        criteria: [
          {
            key: EQuickFilterTranslatedKey.ID,
            operator: EPointSearchOperator.EQUALS,
            values: [Number(keyword)],
          },
        ],
      });
    }
    return newFilters;
  }

  private transformFilter(filter: TSearchFilterRow): TSearchFilter {
    const { item, ...rest } = filter;
    const customWorkspaceId = checkCustomWorkspaceId();
    const isMergedWorkspace =
      customWorkspaceId === EWorkspaces.OVERVIEW ||
      customWorkspaceId === EWorkspaces.TIMELINE ||
      customWorkspaceId === EWorkspaces.REMINDERS;
    const expandedKeys = isMergedWorkspace
      ? this.combineCustomFieldsService.expandCustomFieldIds(customWorkspaceId, rest.keys)
      : rest.keys;
    let newValue = rest.values;
    let newKey = expandedKeys[0];
    let newOperator = rest.operator;

    const type = item?.type || undefined; // TODO KEEP "item type" when deleting old filters
    const isTimeline: boolean = type === EAdvancedFilterType.TIMELINE;

    if (isTimeline) {
      const timelineItem = filter.item as TAdvancedFilterTimeline;

      if (
        timelineItem.date !== EAdvancedFilterTimelineDate.IS_EMPTY &&
        timelineItem.date !== EAdvancedFilterTimelineDate.IS_NOT_EMPTY
      ) {
        newValue = newValue.filter((v) => v).map((v) => new Date(+v).toISOString().split('T')[0]);
      }
    } else {
      const key: string = expandedKeys[0].toLowerCase();

      if (key === EQuickFilterKey.CREATED || key === EQuickFilterKey.UPDATED) {
        newValue = newValue.filter((v) => v).map((v) => +v);
        delete rest.timelinePosition;
      } else if (key === EQuickFilterKey.EXCLUDED_TAGS) {
        newOperator = EPointSearchOperator.NOT_IN;
      }

      newKey = this.translateKey(expandedKeys[0]);
    }

    const isDateFilter: boolean =
      isTimeline ||
      type === EAdvancedFilterType.DATE ||
      type === EAdvancedFilterType.DATE_CREATED ||
      type === EAdvancedFilterType.DATE_UPDATED;

    if (isDateFilter) {
      ({ values: newValue, operator: newOperator } = this.normaliseDateBoundaries(
        newOperator,
        newValue,
        isTimeline,
      ));
    }

    const isNumberType =
      type === EAdvancedFilterType.NUMBER ||
      type === EAdvancedFilterType.PERCENTAGE ||
      type === EAdvancedFilterType.COST ||
      type === EAdvancedFilterType.DURATION ||
      type === EAdvancedFilterType.FORMULA;

    if (isNumberType && newValue.length > 0) {
      newValue = newValue.map((val) => (val != null ? Number(val) : val));
    }

    const isListPartOf: boolean =
      type === EAdvancedFilterType.LIST &&
      (item as { option?: EAdvancedFilterOptions })?.option === EAdvancedFilterOptions.IS_A_PART_OF;

    const multiListOption = (item as { option?: EAdvancedFilterOptions })?.option;
    const isMultiListAnyOrNoneOf: boolean =
      type === EAdvancedFilterType.MULTI_LIST &&
      (multiListOption === EAdvancedFilterOptions.IS_ANY_OF ||
        multiListOption === EAdvancedFilterOptions.IS_NONE_OF);

    const shouldExpandDescendants: boolean = isListPartOf || isMultiListAnyOrNoneOf;

    if (shouldExpandDescendants && newValue.length) {
      const customField = GET_CUSTOM_FIELDS()[expandedKeys[0]];

      if (customField?.subList) {
        newValue = this.expandValuesWithDescendants(newValue, customField.subList);
      }
    }

    const rawKeys = expandedKeys.length > 1 ? [...expandedKeys.slice(1), newKey] : [newKey];
    let valuesByKey = this.resolvePerSiteValues({ ...filter, keys: expandedKeys });
    if (shouldExpandDescendants && valuesByKey) {
      valuesByKey = this.expandValuesByKeyWithDescendants(valuesByKey);
    }
    const keys = valuesByKey ? rawKeys.map((key) => key.toLowerCase()) : rawKeys;

    return {
      operator: newOperator,
      keys,
      values: newValue,
      valuesByKey,
      timelinePosition: rest.timelinePosition,
    };
  }

  /** For merged list/multi-list filters (several site CFs sharing one
   *  column), the incoming `filter.values` only has the *primary* site's
   *  item ids. Translate those to each site's equivalent ids by matching
   *  indexes down the subList, and return a per-key map so each criterion in
   *  the payload carries its own site's ids instead of the primary's. */
  private resolvePerSiteValues(filter: TSearchFilterRow): Record<string, string[]> | undefined {
    const itemType = filter.item?.type;
    const isMergeableType =
      itemType === EAdvancedFilterType.LIST || itemType === EAdvancedFilterType.MULTI_LIST;
    if (!isMergeableType || filter.keys.length <= 1) {
      return undefined;
    }

    const primaryId = filter.item?.id;
    if (!primaryId) {
      return undefined;
    }
    const customFields = GET_CUSTOM_FIELDS();
    const primaryCustomField = customFields[primaryId];
    if (!primaryCustomField?.subList) {
      return undefined;
    }

    const primaryIds = filter.values.filter(isNonEmptyString);
    if (!primaryIds.length) {
      return undefined;
    }

    const positionPaths = primaryIds
      .map((id) => findPositionPath(id, primaryCustomField.subList))
      .filter((path): path is number[] => !!path);
    if (!positionPaths.length) {
      return undefined;
    }

    const result: Record<string, string[]> = {};
    for (const key of filter.keys) {
      const cf = customFields[key];
      const resolvedIds = positionPaths
        .map((path) => findIdByPositionPath(path, cf?.subList))
        .filter((id): id is string => !!id);
      result[key.toLowerCase()] = resolvedIds;
    }
    return result;
  }

  private expandValuesWithDescendants(values: unknown[], subList: TCustomFieldList[]): string[] {
    const expanded = new Set<string>();
    for (const value of values) {
      if (typeof value !== 'string') {
        continue;
      }
      const ids = toggleItemAndDescendants({
        list: subList,
        targetId: value,
        selected: true,
        selectedIds: [],
      });
      for (const id of ids) {
        expanded.add(id);
      }
    }
    return Array.from(expanded);
  }

  private expandValuesByKeyWithDescendants(
    valuesByKey: Record<string, string[]>,
  ): Record<string, string[]> {
    const customFields = GET_CUSTOM_FIELDS();
    const expanded: Record<string, string[]> = {};
    for (const [key, values] of Object.entries(valuesByKey)) {
      const customField = customFields[key];
      if (customField?.subList && values?.length) {
        expanded[key] = this.expandValuesWithDescendants(values, customField.subList);
      } else {
        expanded[key] = values;
      }
    }
    return expanded;
  }

  private normaliseDateBoundaries(
    operator: EPointSearchOperator,
    values: unknown[],
    isTimeline: boolean,
  ): {
    values: unknown[];
    operator: EPointSearchOperator;
  } {
    if (
      (operator === EPointSearchOperator.EQUALS || operator === EPointSearchOperator.NOT_EQUALS) &&
      values[0] != null
    ) {
      if (isTimeline) {
        return { values: [values[0], values[0]], operator: EPointSearchOperator.BETWEEN };
      }

      const start: number = startOfDayUTC(+(values[0] as number));
      const end: number = endOfDayUTC(+(values[0] as number));
      return { values: [start, end], operator: EPointSearchOperator.BETWEEN };
    }

    if (operator === EPointSearchOperator.GREATER_THAN && values[0] != null) {
      if (isTimeline) {
        return {
          values: [this.addDaysToDateString(values[0] as string, 1)],
          operator: EPointSearchOperator.GREATER_THAN_OR_EQUAL,
        };
      }

      return {
        values: [startOfDayUTC(+(values[0] as number), 1)],
        operator: EPointSearchOperator.GREATER_THAN_OR_EQUAL,
      };
    }

    if (operator === EPointSearchOperator.LESS_THAN && values[0] != null) {
      if (!isTimeline) {
        return { values: [startOfDayUTC(+(values[0] as number))], operator };
      }
    }

    if (operator === EPointSearchOperator.GREATER_THAN_OR_EQUAL && values[0] != null) {
      if (!isTimeline) {
        return { values: [startOfDayUTC(+(values[0] as number))], operator };
      }
      return { values: [values[0]], operator };
    }

    if (operator === EPointSearchOperator.LESS_THAN_OR_EQUAL && values[0] != null) {
      if (!isTimeline) {
        return { values: [endOfDayUTC(+(values[0] as number))], operator };
      }
      return { values: [values[0]], operator };
    }

    if (operator === EPointSearchOperator.BETWEEN && values[0] != null && values[1] != null) {
      if (isTimeline) {
        return {
          values: [values[0], values[1]],
          operator,
        };
      }

      return {
        values: [startOfDayUTC(+(values[0] as number)), endOfDayUTC(+(values[1] as number))],
        operator,
      };
    }

    return { values, operator };
  }

  private addDaysToDateString(dateString: string, days: number): string {
    const date: Date = new Date(dateString + 'T00:00:00Z');
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().split('T')[0];
  }

  private translateKey(key: string): string {
    switch (key.toLowerCase()) {
      case EQuickFilterKey.ID:
        return EQuickFilterTranslatedKey.ID;
      case EQuickFilterKey.CREATED_BY:
        return EQuickFilterTranslatedKey.CREATED_BY;
      case EQuickFilterKey.CREATED:
        return EQuickFilterTranslatedKey.CREATED;
      case EQuickFilterKey.UPDATED:
        return EQuickFilterTranslatedKey.UPDATED;
      case EQuickFilterKey.EXCLUDED_TAGS:
        return EQuickFilterTranslatedKey.EXCLUDED_TAGS;
      case EQuickFilterKey.WORKSPACE_REF_ID:
        return EQuickFilterTranslatedKey.WORKSPACE_REF_ID;
      default:
        return key.toLowerCase();
    }
  }

  private emitFiltersChange(): void {
    this._filtersChange$.next(this.filters);
  }
}
